import {
  AsYouType,
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  validatePhoneNumberLength,
  getExampleNumber,
  getCountryCallingCode,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { isoFromDialCode } from './phoneCountries';

/**
 * @param {string} e164
 * @param {string} [defaultIso]
 * @returns {{ iso: string | null, national: string }}
 */
export function splitE164(e164, defaultIso = 'KE') {
  const raw = String(e164 || '').trim();
  if (!raw) return { iso: defaultIso, national: '' };

  const parsed = parsePhoneNumberFromString(raw);
  if (parsed) {
    return { iso: parsed.country || defaultIso, national: parsed.nationalNumber };
  }

  const match = raw.match(/^\+(\d{1,4})(.*)$/);
  if (match) {
    const iso = isoFromDialCode(`+${match[1]}`) || defaultIso;
    return { iso, national: match[2].replace(/\D/g, '') };
  }

  return { iso: defaultIso, national: raw.replace(/\D/g, '') };
}

/**
 * @param {string} iso
 * @param {string} national
 * @returns {string}
 */
export function composeE164(iso, national) {
  const digits = String(national || '').replace(/\D/g, '');
  if (!digits) return '';

  const parsed = parsePhoneNumberFromString(digits, iso);
  if (parsed?.number) return parsed.number;

  // Incomplete entry — keep the selected country's calling code so the picker
  // does not reset when the parent re-syncs an empty or ambiguous value.
  try {
    const callingCode = getCountryCallingCode(iso);
    return `+${callingCode}${digits}`;
  } catch {
    return '';
  }
}

/**
 * Normalise a stored phone for the picker — handles E.164, bare digits, and
 * local trunk prefixes (e.g. 0712… → +254712…).
 * @param {string} value
 * @param {string} [defaultIso]
 * @returns {string}
 */
export function normalizePhoneInput(value, defaultIso = 'KE') {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  if (raw.startsWith('+')) return raw;

  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  if (raw.startsWith('0')) {
    const national = digits.replace(/^0+/, '');
    const e164 = composeE164(defaultIso, national);
    if (e164) return e164;
  }

  const e164 = composeE164(defaultIso, digits);
  if (e164) return e164;

  return raw;
}

/**
 * @param {string} national
 * @param {string} iso
 * @returns {string}
 */
export function formatAsYouType(national, iso) {
  const digits = String(national || '').replace(/\D/g, '');
  if (!digits) return '';
  return new AsYouType(iso).input(digits);
}

/**
 * @param {string} iso
 * @returns {string}
 */
export function examplePlaceholder(iso) {
  try {
    const example = getExampleNumber(iso, examples);
    if (example) return example.formatNational();
  } catch {
    // ignore
  }
  return '712 345 678';
}

/**
 * @param {string} e164
 * @param {{ required?: boolean }} [options]
 * @returns {{ isValid: boolean, e164: string, message: string | null }}
 */
export function validatePhone(e164, options = {}) {
  const { required = false } = options;
  const value = String(e164 || '').trim();

  if (!value) {
    return {
      isValid: !required,
      e164: '',
      message: required ? 'Phone number is required' : null,
    };
  }

  if (!value.startsWith('+')) {
    return {
      isValid: false,
      e164: value,
      message: 'Include country code (e.g. +254…)',
    };
  }

  if (isValidPhoneNumber(value)) {
    const parsed = parsePhoneNumberFromString(value);
    return { isValid: true, e164: parsed?.number || value, message: null };
  }

  const lengthResult = validatePhoneNumberLength(value);
  if (lengthResult === 'TOO_SHORT') {
    return { isValid: false, e164: value, message: 'Phone number is too short' };
  }
  if (lengthResult === 'TOO_LONG') {
    return { isValid: false, e164: value, message: 'Phone number is too long' };
  }

  return { isValid: false, e164: value, message: 'Please enter a valid phone number' };
}
